/** @jsx jsx */
import { Card, Inline, Tiles, jsx } from '@front10/base-elements';
import { withContainer } from '@front10/container-elements';
import { isSSR } from '@front10/utils';
import { TabList, TabPanel, TabPanels, Tabs } from '@reach/tabs';
import { Fragment, useMemo } from 'react';
import { getDate } from '../../../helpers/datetime';
import { Disclaimer } from '../../lab/Disclaimer';
import { DisclaimerLink } from '../../lab/DisclaimerLink';
import { useCheckboxProps, useSelectProps } from '../../lab/utils';
import { FetchAirlines } from './airlines';
import { FetchBlackoutDates } from './blackoutDates';
import { AirportMarketFilter, FetchAirports } from './airports';
import { BusinessRules } from './bookingBusinessRules';
import { BookingComponentsConfigurations } from './bookingComponentsConfigurations';
import { BookingPanel, BookingPanelProps, BookingPanelState } from './BookingPanel';
import { bookingStyles } from './bookingStyles';
import { BookingTab } from './BookingTab';

interface PrePopulation {
  origin?: string;
  destination?: string;
  departureDate?: number;
  returnDate?: number;
}

export interface BookingMaskProps {
  onSubmit: (data: BookingPanelState) => void;
  onOriginChange?: (state: BookingPanelState) => void;
  onJourneyTypeChange?: (state: BookingPanelState) => void;
  onDestinationChange?: (state: BookingPanelState) => void;
  onDepartureDateChange?: (state: BookingPanelState) => void;
  onReturnDateChange?: (state: BookingPanelState) => void;
  onAirlineChange?: (state: BookingPanelState) => void;
  layout?: 'fluid' | 'side-banner';
  bgImage?: string;
  fetchAirports: FetchAirports;
  fetchAirlines: FetchAirlines;
  fetchBlackoutDates: FetchBlackoutDates;
  filterFields?: AirportMarketFilter;
  businessRules: BusinessRules;
  passengersRules?: Array<string>;
  prePopulation: PrePopulation;
  componentsConfiguration: BookingComponentsConfigurations & {
    panels: Array<BookingPanelProps & BookingComponentsConfigurations>;
  };
  originDestinationLogic?: string;
  activePanel?: number;
  setActivePanel?: (idx: number) => void;
}

export function BookingMask(props: BookingMaskProps) {
  const sideBanner = props.layout === 'side-banner';
  const prePopulation = props.prePopulation;

  const businessRules = props.businessRules;

  const journeyTypes = useSelectProps(businessRules.journeyTypes);
  const carTypes = useSelectProps(businessRules.carTypes);
  const dropOff = useSelectProps(businessRules.dropOff);
  const hotelCategories = useSelectProps(businessRules.hotelCategories);
  const driverAges = useSelectProps(businessRules.driverAges);
  const nationalities = useSelectProps(businessRules.nationalities);

  const additionalHotelRules = useCheckboxProps(businessRules.additionalHotelRules);
  const additionalFlightRules = useCheckboxProps(businessRules.additionalFlightRules);
  const additionalCarRules = useCheckboxProps(businessRules.additionalCarRules);
  const preventBottomDisplacement =
    props.componentsConfiguration?.preventBottomDisplacement === true ? true : false;

  const handleTabsChange = (index: number) => {
    props?.setActivePanel?.(index);
  };
  const dates = useMemo(() => {
    return {
      start: getDate(prePopulation.departureDate),
      end: getDate(prePopulation.returnDate),
      startTime: businessRules.timePickers?.defaultValue,
      endTime: businessRules.timePickers?.defaultValue
    };
  }, [
    businessRules.timePickers?.defaultValue,
    prePopulation.departureDate,
    prePopulation.returnDate
  ]);

  const ssr = isSSR();
  const panels = props.componentsConfiguration.panels ?? [];
  const disclaimer = props.componentsConfiguration.disclaimer;

  const isOnePanel = panels.length === 1;
  const PanelComponent = isOnePanel ? Fragment : TabPanel;

  const panelsComponent = panels.map((panel, index) => {
    let rules;
    if (panel.type === 'flight') {
      rules = additionalFlightRules;
    } else if (panel.type === 'car') {
      rules = additionalCarRules;
    } else if (panel.type === 'hotel') {
      rules = additionalHotelRules;
    }

    const airlinesSelector = {
      airlinesList: props.componentsConfiguration.airlinesSelector?.airlinesList ?? [],
      enabled:
        (props.componentsConfiguration.airlinesSelector?.enabled || panel.enableAirlineFilter) ??
        false
    };

    return (
      <PanelComponent
        key={panel.name}
        {...(!isOnePanel && { tabIndex: -1, ...(index !== 0 && ssr && { hidden: true }) })}
      >
        <Card pl="3" pr="3" pt="4" pb="4" sx={{ overflow: 'visible', height: '100%' }}>
          <BookingPanel
            activeTab={props.activePanel}
            currentTab={index}
            layout={props.layout}
            onSubmit={props.onSubmit}
            onOriginChange={props.onOriginChange}
            onAirlineChange={props.onAirlineChange}
            onJourneyTypeChange={props.onJourneyTypeChange}
            onDestinationChange={props.onDestinationChange}
            onDepartureDateChange={props.onDepartureDateChange}
            onReturnDateChange={props.onReturnDateChange}
            fetchAirports={props.fetchAirports}
            filterFields={props.filterFields}
            travelClasses={businessRules.travelClasses}
            journeyTypes={journeyTypes}
            defaultOrigin={props.prePopulation?.origin}
            defaultDestination={props.prePopulation?.destination}
            defaultDates={dates}
            rooms={businessRules.rooms}
            passengers={businessRules.travelers}
            enableLegend={panel.enableLegend}
            passengersRules={props.passengersRules}
            carTypes={carTypes}
            type={panel.type}
            dropOff={dropOff}
            additionalRules={rules}
            labels={panel.labels ?? {}}
            componentsConfigurations={props.componentsConfiguration}
            overrideComponentsConfigurations={panel}
            blackoutDates={panel.blackoutDates}
            maxTripDuration={panel.maxTripDuration}
            minTripDuration={panel.minTripDuration}
            hotelCategories={hotelCategories}
            ages={driverAges}
            nationalities={nationalities}
            airlines={airlinesSelector}
            fetchAirlines={props.fetchAirlines}
            fetchBlackoutDates={props.fetchBlackoutDates}
            advancedSelector={panel.advancedSelector}
            passengerSelector={panel.passengerSelector}
            showAdvancedFilter={panel.showAdvancedFilter}
            promoCode={businessRules.promoCode}
            originDestinationLogic={props.originDestinationLogic}
            businessRules={props.businessRules}
            excludeBasicEconomy={businessRules.excludeBasicEconomy}
            preventBottomDisplacement={preventBottomDisplacement}
            allowHoverStyleInRange={props.componentsConfiguration?.allowHoverStyleInRange}
          />

          {disclaimer?.enabled ? (
            disclaimer?.links ? (
              <Tiles variant="layout.disclaimerLinks" columns={[1, 3, 3, 5]} rowGap={2} colGap={2}>
                {disclaimer?.enabled ? (
                  <Disclaimer label={'cmp-booking-mask-disclaimer'} {...disclaimer} />
                ) : null}
                {disclaimer?.links?.map((link) => (
                  <DisclaimerLink {...link} />
                ))}
              </Tiles>
            ) : (
              <Disclaimer label={'cmp-booking-mask-disclaimer'} {...disclaimer} />
            )
          ) : null}
        </Card>
      </PanelComponent>
    );
  });

  const panelContainerStyles = {
    maxWidth: sideBanner ? 'tabListWidth' : 'xl',
    gridColumn: sideBanner ? '2' : '3',
    gridRow: sideBanner ? '2 / span 2' : '2',
    width: '100%',
    height: '100%',
    justifySelf: 'center',
    '[data-reach-tab]': {
      variant: 'buttons.group'
    },
    '[data-selected]': {
      variant: 'buttons.groupSelected'
    }
  } as const;

  return (
    <Inline
      variant="layout.container"
      align={sideBanner ? 'start' : 'center'}
      sx={(theme: any) => {
        const dimensions = theme.layout?.mask ?? {};
        const {
          marginBlockStart = '60px',
          marginBlockEnd = '30px',
          marginInline = '15px',
          formHeight = 'auto'
        } = dimensions;
        return {
          minWidth: '100%',
          backgroundPosition: 'center',
          display: 'grid',
          gridTemplateColumns: `${marginInline} auto 1fr ${marginInline}`,
          gridTemplateRows: `${marginBlockStart} ${formHeight} ${marginBlockEnd}`,
          ':before': {
            content: "''",
            gridColumn: '1 / span 4',
            gridRow: sideBanner ? '1 / span 2' : '1 / span 3',
            ...(!!props.bgImage && {
              backgroundImage: `url('${props.bgImage}')`,
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat'
            })
          }
        };
      }}
    >
      {isOnePanel ? (
        <div sx={panelContainerStyles}>{panelsComponent}</div>
      ) : (
        <Tabs
          sx={panelContainerStyles}
          defaultIndex={0}
          index={props.activePanel}
          onChange={handleTabsChange}
        >
          <TabList>
            <Inline gap="2px" sx={{ maxWidth: 'tabListWidth' }}>
              {panels.length > 1 &&
                panels.map((panel) => (
                  <BookingTab
                    key={panel.name}
                    labelKey={panel.label}
                    defaultLabel={panel.label}
                    icon={panel.icon}
                  />
                ))}
            </Inline>
          </TabList>
          <TabPanels sx={{ pt: '5px' }}>{panelsComponent}</TabPanels>
        </Tabs>
      )}
    </Inline>
  );
}

export const BookingMaskContainer = withContainer(BookingMask, bookingStyles);
